import { useState, useRef } from "react";

export const ImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const thumbnailRef = useRef(null);
  const imgContainerRef = useRef(null);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // State zoom
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomCenter, setZoomCenter] = useState({ x: 0.5, y: 0.5 });

  // Di chuyển slide
  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
    setIsZoomed(false);
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1,
    );
    setIsZoomed(false);
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
    setIsZoomed(false);
  };

  // Kéo thumbnail
  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    startX.current = e.pageX || e.touches?.[0].pageX;
    scrollLeft.current = thumbnailRef.current.scrollLeft;
  };
  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const x = e.pageX || e.touches?.[0].pageX;
    const walk = (x - startX.current) * 2;
    thumbnailRef.current.scrollLeft = scrollLeft.current - walk;
  };
  const handleMouseUp = () => {
    isDragging.current = false;
  };
  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  // Double click vào ảnh lớn sẽ zoom tập trung đúng điểm
  const handleDoubleClick = (e) => {
    if (!isZoomed) {
      // Lấy vị trí chuột trong container
      const rect = imgContainerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setZoomCenter({ x, y });
      setIsZoomed(true);
    } else {
      setIsZoomed(false);
    }
  };

  return (
    <div
      className="carousel-container mb-3"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "800px",
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      {/* Main Carousel */}
      <div
        className="carousel-slides"
        style={{
          display: "flex",
          transition: "transform 0.5s ease-in-out",
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {images.map((image, index) => (
          <div
            key={index}
            ref={index === currentIndex ? imgContainerRef : null}
            style={{
              width: "100%",
              height: "400px",
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "#f5f5f5",
              position: "relative",
              overflow: "hidden",
              cursor:
                isZoomed && currentIndex === index ? "zoom-out" : "zoom-in",
            }}
            onDoubleClick={
              index === currentIndex ? handleDoubleClick : undefined
            }
          >
            <img
              src={image}
              alt={`Slide ${index}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "6px",
                userSelect: "none",
                transition: "transform 0.4s cubic-bezier(.5,1.8,.55,.9)",
                cursor:
                  isZoomed && currentIndex === index ? "zoom-out" : "zoom-in",
                // Tạo hiệu ứng zoom centered vào điểm click
                transform:
                  isZoomed && currentIndex === index
                    ? `scale(2) translate(${
                        (0.5 - zoomCenter.x) * 100
                      }%, ${(0.5 - zoomCenter.y) * 100}%)`
                    : "scale(1) translate(0,0)",
              }}
              draggable={false}
            />
          </div>
        ))}
      </div>

      {/* Current Slide Indicator */}
      <div
        className="carousel-indicator"
        style={{
          position: "absolute",
          top: "360px",
          right: "10px",
          background: "rgba(0, 0, 0, 0.7)",
          color: "#fff",
          padding: "5px 10px",
          borderRadius: "5px",
          fontSize: "14px",
        }}
      >
        {currentIndex + 1} / {images.length}
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={prevSlide}
        className="carousel-control-prev"
        style={{
          position: "absolute",
          left: "0",
          top: "150px",
          height: "100px",
          background: "transparent",
          color: "rgba(0,0,0,0.5)",
          border: "none",
          padding: "10px",
          fontSize: "30px",
          cursor: "pointer",
        }}
      >
        ❮
      </button>
      <button
        onClick={nextSlide}
        className="carousel-control-next"
        style={{
          position: "absolute",
          right: "0",
          top: "150px",
          height: "100px",
          background: "transparent",
          color: "rgba(0,0,0,0.5)",
          border: "none",
          padding: "10px",
          fontSize: "30px",
          cursor: "pointer",
        }}
      >
        ❯
      </button>

      {/* Thumbnail Carousel */}
      <div
        ref={thumbnailRef}
        className="thumbnail-carousel"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "10px",
          gap: "10px",
          overflowX: "auto",
          cursor: "grab",
          padding: "0 1px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
      >
        {images.map((image, index) => (
          <div
            key={index}
            onClick={() => handleThumbnailClick(index)}
            style={{
              cursor: "pointer",
              border:
                currentIndex === index
                  ? "1px solid #439EFFFF"
                  : "1px solid #ddd",
              borderRadius: "5px",
              padding: currentIndex === index ? "5px" : "0",
              overflow: "hidden",
              width: "50px",
              height: "50px",
              flexShrink: 0,
              background: currentIndex === index ? "#f0f8ff" : "transparent",
            }}
          >
            <img
              src={image}
              alt={`Thumbnail ${index}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                userSelect: "none",
              }}
              draggable={false}
            />
          </div>
        ))}
      </div>

      <style>
        {`
          .thumbnail-carousel::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </div>
  );
};
